// React
import React, { FC, useContext, useState, useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Context
import UserDetailsContext from 'Context/userDetailsContext';

// GraphQL
// This will need to be dealt with later, installing this
// broke the app
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery } from '@apollo/react-hooks';

// Styles
import injectSheet from 'react-jss';

// Components
import { Loading } from 'Components/Loading';
import { Center } from 'Components/Helpers';

// Containers
import { GoodDataDashboardContainer } from 'Containers/GoodData/containers';

// Actions
import {
  setPlatformInfoEventValue,
  setFilterValueChangeEventValue,
  setGoodDataEventValue,
  setDateRangeValue,
} from 'Containers/GoodData/actions/goodData.actions';

// Utilities
import {
  modifyGoodDataFilterContext,
  isUserOnOrPastSafariTwelve,
  useIwcDataWarning,
} from 'Containers/GoodData/utilities';
import { getRelativeDateFromToday } from 'Utils/getRelativeDateFromToday';
import { areEqual } from 'Utils/areEqual';
import { formatDatesForGoodData } from 'Containers/GoodData/utilities/formatDatesForGoodData';
import { formatDatesForCalendar } from 'Containers/GoodData/utilities/formatDatesForCalendar';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import withErrorBoundary from 'HOC/ErrorBoundary';
import { findDateFromDays } from '../../../../../utils/findDateFromDays';
import styleSheet from './styles';
import goodDataGql from './gqlQuery';

interface Props {
  classes?: any;
}

const YourEDUVSNetwork: FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const {
    goodDataEvent,
    platformInfoEvent,
    filterValueChangeEvent,
    dateRange,
    userBrowser,
  } = useSelector((selectors: any) => selectors.goodData, areEqual);
  const { selection, fromDate, toDate } = dateRange;

  const { classes } = props;

  const {
    actions: { track },
  } = useContext(ProductAnalyticsContext);

  useEffect(() => {
    // Combined Analytics event tracking
    track('your_edu_vs_network_page_viewed');
  }, []);

  // State that holds the GraphQL data.
  const [gdGQLData, setGDGQLData] = useState(undefined);

  // Use this state to keep track of how many label.value.change
  // events have occured in order to know how long to display our
  // loading animation
  const [isLoading, setIsLoading] = useState(true);

  // This constant is the dashboard name as the back-end knows it.
  const YOUR_EDU_VS_NETWORK_DASHBOARD_NAME = 'your_edu_vs_network';

  // This constant is the label that is modified in modifyGoodDataFilterContext
  // calls for this dashboard.
  const DATE_FILTER_LABEL = 'event_date.date.day.us.mm_dd_yyyy';

  const INST_FILTER_LABEL =
    'label.cp_institution_daily_summary_with_network.institution_id';

  // Other filters
  const INSTITUTION_SIZE_LABEL =
    'label.youredu_location_experience_sector_size_filter_v2.institution_size';
  const INSTITUTION_SECTOR_LABEL =
    'label.youredu_location_experience_sector_size_filter_v2.institution_sector';
  const YOUR_CONTENT_TO_COMPARE_LABEL =
    'label.youredu_location_experience_sector_size_filter_v2.location_name_experience_type';
  const INSTITUTION_REGION_LABEL =
    'label.youredu_location_experience_sector_size_filter_v2.institution_region_division';

  const userDetails = useContext(UserDetailsContext);

  // GraphQL User.GoodDataClaims Query
  const { data: goodDataData } = useQuery(goodDataGql, {
    variables: {
      id: userDetails.userId,
      accountId: userDetails.accountId,
      base64Encoded: true,
      instId: userDetails.instId,
    },
    skip: gdGQLData,
  });

  // End loading early if we know there is nothing loading for
  // aesthetic purposes
  useEffect(() => {
    if (userBrowser && isUserOnOrPastSafariTwelve(userBrowser)) {
      setIsLoading(false);
    }
  }, [userBrowser]);

  // useEffect to update state when the goodDataData changes.  Should
  // really only happen once.
  useEffect(() => {
    if (goodDataData && !areEqual(goodDataData, gdGQLData)) {
      setGDGQLData(goodDataData);
    }
  }, [goodDataData]);

  // The first time the dashboard has loaded, change the filter context to 30 Days,
  // (Product requirement).
  useEffect(() => {
    if (platformInfoEvent) {
      modifyGoodDataFilterContext(YOUR_EDU_VS_NETWORK_DASHBOARD_NAME, [
        {
          label: DATE_FILTER_LABEL,
          from: fromDate,
          to: toDate,
          type: 'date',
        },
        {
          label: INST_FILTER_LABEL,
          value: gdGQLData.institution.instGuid,
        },
      ]);
      setPlatformInfoEventValue(dispatch, false);
    }
  }, [platformInfoEvent]);

  // Use this to listen to user changing the Filter Context for Combined Analytics
  useEffect(() => {
    if (
      filterValueChangeEvent &&
      goodDataEvent &&
      goodDataEvent.type === 'filter.value.changed'
    ) {
      /*
        This fires off when the page loads and has all the events below.
        We only want send a Combined Analytics event, if the length of data is 1. 
        This indicates that one of the inputs have been used, instead of 
        the page loading on startup.
      */
      if (goodDataEvent.event.gdc.data.length === 1) {
        // Combined Analytics Variables
        let filterValue = '';
        let eventName = '';
        goodDataEvent.event.gdc.data.forEach((filter: any) => {
          // This filter object contains two properties, filter.label and filter.value.  These properties are named in a self-explanatory manner.
          // Use these properties with Combined Analytics
          switch (filter.label) {
            case YOUR_CONTENT_TO_COMPARE_LABEL:
              filterValue = filter.value;
              eventName = 'your_content_to_compare_filter_selected';
              break;
            case INSTITUTION_SECTOR_LABEL:
              filterValue = filter.value;
              eventName = 'institution_sector_filter_selected';
              break;
            case INSTITUTION_SIZE_LABEL:
              filterValue = filter.value;
              eventName = 'institution_size_filter_selected';
              break;
            case INSTITUTION_REGION_LABEL:
              filterValue = filter.value;
              eventName = 'institution_region_filter_selected';
              break;
            case DATE_FILTER_LABEL:
              break;
            default:
              break;
          }
        });

        if (filterValue !== '' && eventName !== '')
          // Combined Analytics event tracking
          track(eventName, {
            screen: 'My EDU vs Network',
            filter_value: filterValue,
          });
      }

      setGoodDataEventValue(dispatch, undefined);
      setFilterValueChangeEventValue(dispatch, false);
    }
  }, [filterValueChangeEvent, goodDataEvent]);

  // Loading timeout to ensure we will show GoodData even if we have an event issue
  setTimeout(() => {
    if (isLoading) {
      setIsLoading(false);
    }
  }, 5000);

  // Anytime a dropdown change happens the event filter context change
  // is propogated to the dashboard.
  // Anytime a dropdown change happens the event filter context change
  // is propogated to both dashboards.
  const handleDropdownChange = (event: any) => {
    if (!areEqual(event.target, { label: 'Custom Date', value: 0 })) {
      setDateRangeValue(dispatch, {
        selection: event.target,
        fromDate: findDateFromDays(event.target.value),
        toDate: getRelativeDateFromToday(1),
      });
    } else {
      setDateRangeValue(dispatch, {
        selection: event.target,
        fromDate,
        toDate,
      });
    }
  };

  const handleCalendarChange = (
    selectedStartDate: string,
    selectedChangeEndDate: string
  ) => {
    setDateRangeValue(dispatch, {
      selection,
      fromDate: formatDatesForGoodData(selectedStartDate),
      toDate: formatDatesForGoodData(selectedChangeEndDate),
    });
  };

  useEffect(() => {
    modifyGoodDataFilterContext(YOUR_EDU_VS_NETWORK_DASHBOARD_NAME, [
      {
        label: DATE_FILTER_LABEL,
        from: fromDate,
        to: toDate,
        type: 'date',
      },
    ]);
  }, [fromDate, toDate]);

  // Show IWC data warning message if IWC data is not available in selected fromDate
  useIwcDataWarning(dispatch, fromDate);

  return (
    <div
      className={
        userBrowser &&
        !isUserOnOrPastSafariTwelve(userBrowser) &&
        classes.backgroundContainer
      }
    >
      {isLoading && (
        <div className={classes.loader}>
          <Center>
            <Loading />
          </Center>
        </div>
      )}
      <div
        className={
          userBrowser &&
          !isUserOnOrPastSafariTwelve(userBrowser) &&
          classes.inputHider
        }
      />
      <GoodDataDashboardContainer
        title="Your EDU vs. Netowrk Dashboard"
        name={YOUR_EDU_VS_NETWORK_DASHBOARD_NAME}
        id={YOUR_EDU_VS_NETWORK_DASHBOARD_NAME}
        gdData={gdGQLData}
        width="940px"
        height="1713px"
        border="none"
        errorContainerStyles={{
          width: '1074px',
          height: '744px',
        }}
        handleDropdownChange={handleDropdownChange}
        startDate={formatDatesForCalendar(fromDate)}
        endDate={formatDatesForCalendar(toDate)}
        selectedOption={selection}
        handleCalendarChange={handleCalendarChange}
        useGoodDataDropdown
        goodDataDropdownStyling={{
          height: '136px',
          width: '987px',
          backgroundColor: '#FFFFFF',
          position: 'relative',
          marginBottom: '-47px',
          zIndex: 8,
        }}
        marginLeft="17px"
      />
    </div>
  );
};

YourEDUVSNetwork.defaultProps = {
  classes: {},
};

export default withErrorBoundary(injectSheet(styleSheet)(YourEDUVSNetwork));
